"use client"

import type { User } from "better-auth"
import type { Member } from "better-auth/plugins/organization"
import { Refresh as Loader2 } from "@papier/ui/components/icons"
import { type ComponentProps, useContext, useMemo, useState } from "react"

import { AuthUIContext } from "../../lib/auth-ui-provider"
import { getLocalizedError } from "../../lib/utils"
import { cn } from "@papier/ui/lib/utils"
import type { AuthLocalization } from "../../localization/auth-localization"
import type { SettingsCardClassNames } from "../settings/shared/settings-card"
import { Button } from "@papier/ui/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@papier/ui/components/ui/dialog"
import { MemberCell } from "./member-cell"

export interface RemoveMemberDialogProps extends ComponentProps<typeof Dialog> {
    classNames?: SettingsCardClassNames
    localization?: AuthLocalization
    member: Member & { user?: Partial<User> | null }
    onOpenChange?: (open: boolean) => void
}

export function RemoveMemberDialog({
    member,
    classNames,
    localization: localizationProp,
    onOpenChange,
    ...props
}: RemoveMemberDialogProps) {
    const {
        authClient,
        hooks: { useListMembers },
        localization: contextLocalization,
        toast
    } = useContext(AuthUIContext)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const { refetch } = useListMembers({
        query: { organizationId: member.organizationId }
    })

    const [isRemoving, setIsRemoving] = useState(false)
    const removeMember = async () => {
        setIsRemoving(true)

        try {
            await authClient.organization.removeMember({
                memberIdOrEmail: member.id,
                organizationId: member.organizationId,
                fetchOptions: { throw: true }
            })

            toast({
                variant: "success",
                message: localization.REMOVE_MEMBER_SUCCESS
            })

            await refetch?.()

            onOpenChange?.(false)
        } catch (error) {
            toast({
                variant: "error",
                message: getLocalizedError({ error, localization })
            })
        }

        setIsRemoving(false)
    }

    return (
        <Dialog onOpenChange={onOpenChange} {...props}>
            <DialogContent
                className={classNames?.dialog?.content}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <DialogHeader className={classNames?.dialog?.header}>
                    <DialogTitle
                        className={cn("text-lg md:text-xl", classNames?.title)}
                    >
                        {localization.REMOVE_MEMBER}
                    </DialogTitle>

                    <DialogDescription
                        className={cn(
                            "text-xs md:text-sm",
                            classNames?.description
                        )}
                    >
                        {localization.REMOVE_MEMBER_CONFIRM}
                        <span className="mt-2 block">Their account will lose access to this organization and its MCP connections. Organization API keys remain active for shared integrations. Revoke or rotate any keys this person could access in API Keys.</span>
                    </DialogDescription>
                </DialogHeader>

                <MemberCell
                    className={classNames?.cell}
                    member={member}
                    localization={localization}
                    hideActions
                />

                <DialogFooter className={classNames?.dialog?.footer}>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange?.(false)}
                        className={cn(
                            classNames?.button,
                            classNames?.outlineButton
                        )}
                        disabled={isRemoving}
                    >
                        {localization.CANCEL}
                    </Button>

                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => { void removeMember() }}
                        className={cn(
                            classNames?.button,
                            classNames?.destructiveButton
                        )}
                        disabled={isRemoving}
                    >
                        {isRemoving && <Loader2 className="animate-spin" />}

                        {localization.REMOVE_MEMBER}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
